import { Plus, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEditorStore } from '../../stores/editorStore';
import { useProjectStore } from '../../stores/projectStore';
import { Button } from '../atoms/Button';
import type { SceneWithShots, Shot } from '../../types/database';

interface ShotNavigatorProps {
  scenes: SceneWithShots[];
}

// Sortable Shot Item Component
interface SortableShotItemProps {
  shot: Shot;
  sceneIdx: number;
  shotIdx: number;
  isActive: boolean;
  onClick: () => void;
  renderThumbnail: (shot: Shot) => React.ReactNode;
}

function SortableShotItem({ shot, sceneIdx, shotIdx, isActive, onClick, renderThumbnail }: SortableShotItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: shot.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isDragging ? 'opacity-50' : ''}`}
    >
      <button
        onClick={onClick}
        className={`w-32 aspect-video rounded-xl overflow-hidden border-2 transition-all ${
          isActive
            ? 'border-blue-500 scale-105'
            : 'border-transparent opacity-40 hover:opacity-100'
        }`}
      >
        {renderThumbnail(shot)}
      </button>
      
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute -top-2 -right-2 w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
      >
        <GripVertical size={12} className="text-white" />
      </div>
      
      {/* Shot Number */}
      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-[8px] font-bold text-white">
        {sceneIdx + 1}.{shotIdx + 1}
      </div>
    </div>
  );
}

export function ShotNavigator({ scenes }: ShotNavigatorProps) {
  const { 
    manualSceneIdx, 
    manualShotIdx,
    currentSceneIdx,
    currentShotIdx,
    isPlaying,
    goToShot,
    setIsPlaying 
  } = useEditorStore();
  
  const { addShot, reorderShots } = useProjectStore();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleShotClick = (sceneIdx: number, shotIdx: number) => {
    goToShot(sceneIdx, shotIdx);
    setIsPlaying(false);
  };

  const handleAddShot = (sceneId: string) => {
    const newShot: Shot = {
      id: `temp-${Date.now()}`,
      scene_id: sceneId,
      order_index: 0,
      name: 'New Shot',
      narration_text: '',
      duration_sec: 5,
      visual_type: 'image',
      media_url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
      ai_prompt: '',
      thumbnail_url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
      text: null,
      text_enabled: false,
    };
    addShot(sceneId, newShot);
  };

  const handleDragEnd = (sceneId: string, event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const scene = scenes.find(s => s.id === sceneId);
      if (!scene) return;

      const oldIndex = scene.shots.findIndex(s => s.id === active.id);
      const newIndex = scene.shots.findIndex(s => s.id === over.id);

      const newShots = arrayMove(scene.shots, oldIndex, newIndex);
      const shotIds = newShots.map(s => s.id);
      
      reorderShots(sceneId, shotIds);
    }
  };

  const renderThumbnail = (shot: Shot) => {
    if (shot.visual_type === 'video' && shot.media_url) {
      return (
        <div className="w-full h-full relative bg-black">
          <video
            src={shot.media_url}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
          />
          <div className="absolute top-1 right-1 bg-blue-600 text-white p-1 rounded-full scale-75 shadow-lg border border-white/20">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
        </div>
      );
    }
    
    return (
      <div className="w-full h-full relative">
        <img
          src={shot.thumbnail_url || shot.media_url || ''}
          alt={shot.name}
          className="w-full h-full object-cover"
        />
        {shot.visual_type === 'image' && (
          <div className="absolute top-1 right-1 bg-amber-600 text-white p-1 rounded-full scale-75 shadow-lg border border-white/20">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex gap-3 p-4 bg-white/5 rounded-3xl border border-white/5 overflow-x-auto custom-scrollbar">
      {scenes.map((scene, sIdx) => (
        <div key={scene.id} className="flex gap-2 items-center flex-shrink-0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => handleDragEnd(scene.id, event)}
          >
            <SortableContext
              items={scene.shots.map(s => s.id)}
              strategy={horizontalListSortingStrategy}
            >
              {scene.shots.map((shot, kIdx) => {
                const activeSceneIdx = isPlaying ? currentSceneIdx : manualSceneIdx;
                const activeShotIdx = isPlaying ? currentShotIdx : manualShotIdx;
                
                return (
                  <SortableShotItem
                    key={shot.id}
                    shot={shot}
                    sceneIdx={sIdx}
                    shotIdx={kIdx}
                    isActive={activeSceneIdx === sIdx && activeShotIdx === kIdx}
                    onClick={() => handleShotClick(sIdx, kIdx)}
                    renderThumbnail={renderThumbnail}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleAddShot(scene.id)}
            className="w-10 h-10 rounded-full bg-white/5 text-slate-500 hover:bg-blue-600 hover:text-white flex-shrink-0"
          >
            <Plus size={18} />
          </Button>
        </div>
      ))}
    </div>
  );
}
